import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
  Textarea,
  toast,
} from "@mindstonehq/ui";
import { AnnotatePanelProps } from "../annotate";
import React from "react";
import useAuthCheck from "~/hooks/check-authenticated";
import metrics from "~/utils/metrics";
import { useMutation } from "@apollo/client/react";
import type { UpdatePrivateNoteMutation, UpdatePrivateNoteMutationVariables } from "~/graphql/codegen/graphql";
import { UPDATE_PRIVATE_NOTE_MUTATION } from "../annotate-mutations";
import { HighlightBlockquote } from "./blockquote";
import { IHighlightCommon } from "~/typings/annotate/annotation";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export default function ResourceNotePanel({
  article,
  close,
  annotations,
  refresh,
}: AnnotatePanelProps) {
  const { t } = useLingui();
  const checkAuth = useAuthCheck();
  const allPrivateNotes = annotations.highlights.filter((h) => !!h.privateNote);
  const [notesText, setNotesText] = React.useState<{
    [key: string]: string;
  } | null>(null);
  const [lastUpdated, setLastUpdated] = React.useState<Date>();
  const [updatePrivateNote] = useMutation<UpdatePrivateNoteMutation, UpdatePrivateNoteMutationVariables>(UPDATE_PRIVATE_NOTE_MUTATION);
  const timeoutsRef = React.useRef<{ [key: string]: number } | null>(null);

  React.useEffect(() => {
    let notes: { [key: string]: string } = {};
    annotations.highlights.forEach((highlight) => {
      if (highlight.privateNote) {
        notes[highlight.id] = highlight.privateNote.content;
      }
    });
    setNotesText(notes);
  }, []);

  const handleNoteUpdate = (content: string, highlightID: string) => {
    let updatedNotes = { ...notesText };
    updatedNotes[highlightID] = content;
    setNotesText(updatedNotes);
    if (timeoutsRef?.current && timeoutsRef?.current[highlightID])
      clearTimeout(timeoutsRef?.current[highlightID]);
    if (!timeoutsRef?.current) timeoutsRef.current = {};
    timeoutsRef.current[highlightID] = window.setTimeout(() => {
      saveNote(content, highlightID);
    }, 500);
  };

  const saveNote = (content: string, highlightID: string) => {
    const authenticated = checkAuth(
      t(msg`You need to be logged in to add a note`),
      `${location.href}`,
      "private_note_edit",
    );
    if (!authenticated) {
      return Promise.resolve("");
    }
    metrics.track("all_notes_note_update", {
      article_id: article.id,
      view: "annotate",
    });
    return updatePrivateNote({
      variables: {
        articleID: article.id,
        highlightID: highlightID,
        content,
      },
    })
      .then((res) => {
        if (!res.data) {
          // eslint-disable-next-line lingui/no-unlocalized-strings -- dev-only thrown string
          throw "data missing";
        }
        setTimeout(() => {
          refresh();
        }, 1000);
        setLastUpdated(new Date());
        return (res.data.updatePrivateNote as IHighlightCommon).id;
      })
      .catch((error) => {
        console.error(error);
        toast.error(t(msg`We couldn't save this note. Please try again later.`));
        throw error;
      });
  };

  function handleDone() {
    setTimeout(() => {
      close();
    }, 300);
  }

  return (
    <DialogContent className="max-h-[90vh] flex flex-col md:w-[550px] gap-0">
      <DialogHeader>
        <DialogTitle className="flex items-center flex-row space-x-2">
          <Trans>My private notes</Trans>{" "}
          <span className="text-placeholder-foreground ml-1">
            ({allPrivateNotes.length}){" "}
          </span>
          {lastUpdated && (
            <span className="text-placeholder-foreground">
              {" "}
              {lastUpdated.getHours()}:
              {String(lastUpdated.getMinutes()).padStart(2, "0")}
            </span>
          )}
        </DialogTitle>
        <Separator className="bg-highlight" />
      </DialogHeader>

      {allPrivateNotes.length > 0 ? (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex flex-col flex-1 overflow-y-auto divide-y divide-border">
            {allPrivateNotes.map((highlight, index) => (
              <div className="space-y-4 pt-6 pr-6 pl-1 pb-4 -mr-6">
                {/* {index !== 0 && <Separator className="bg-highlight mb-2" />} */}
                <HighlightBlockquote
                  text={highlight?.text || ""}
                  model={highlight?.model}
                />
                <div className="flex flex-col flex-1 justify-between space-y-5">
                  <Textarea
                    className="flex-1"
                    value={notesText ? notesText[highlight.id] : ""}
                    onChange={(e) =>
                      handleNoteUpdate(e.target.value, highlight.id)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              className="mt-4 self-end"
              onClick={handleDone}
            >
              <Trans>Done</Trans>
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <div className="text-muted-foreground p-4">
          <Trans>No private notes for this article</Trans>
        </div>
      )}
    </DialogContent>
  );
}
